import React, { useMemo, useRef } from 'react';
import { Form, Row, Col, Input, Divider, Checkbox, Select, Spin } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { searchClients, clearClientSuggestions, fetchClientById } from '../OfflineBookingActions';
import '../../../../assets/scss/components/OfflineBooking/ClientDetailsForm.scss';

const ClientDetailsForm = ({ form }) => {
  const dispatch = useDispatch();
  const { clientSuggestions = [], loadingClients = false } = useSelector(state => state.offlineBooking || {});

  const debounceRef = useRef();

  // Phone validation: allow +, digits, spaces, parentheses, dashes; 7-15 digits total
  const validatePhone = (_, value) => {
    if (!value || !value.trim()) return Promise.resolve(); // optional
    const digits = value.replace(/\D/g, '');
    if (digits.length < 7 || digits.length > 15) {
      return Promise.reject(new Error('Enter a valid phone number (7-15 digits)'));
    }
    if (!/^[+\d ()-]+$/.test(value)) {
      return Promise.reject(new Error('Only digits, +, spaces, (), and - are allowed'));
    }
    return Promise.resolve();
  };

  const handleSearch = (value) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (value && value.trim().length >= 2) {
        dispatch(searchClients(value.trim()));
      } else {
        dispatch(clearClientSuggestions());
      }
    }, 300);
  };

  const handleSelectClient = async (userId) => {
    const hint = clientSuggestions.find(u => (u.id === userId || u.userId === userId));
    const nameFromHint = hint ? (hint.name || [hint.firstName, hint.lastName].filter(Boolean).join(' ')) : '';
    // Try to fetch full details (to get email/phone)
    const fetchResult = await dispatch(fetchClientById(userId));
    const full = fetchResult?.payload || fetchResult;
    const email = full?.email || full?.username || hint?.email || hint?.username || '';
    
    // Extract phone from phoneNumbers array (User model has List<Phone> phoneNumbers)
    let phone = '';
    if (full?.phoneNumbers && Array.isArray(full.phoneNumbers) && full.phoneNumbers.length > 0) {
      // Get the primary phone or first phone number
      const primaryPhone = full.phoneNumbers.find(p => p.primary) || full.phoneNumbers[0];
      phone = primaryPhone?.number || '';
    }

    if (form && typeof form.setFieldsValue === 'function') {
      form.setFieldsValue({
        clientUserId: userId,
        clientName: nameFromHint || full?.name || [full?.firstName, full?.lastName].filter(Boolean).join(' '),
        clientEmail: email,
        clientPhone: phone,
        useExisting: true
      });
    }
  };

  const options = useMemo(() => {
    return (clientSuggestions || []).map(u => {
      const id = u.id || u.userId;
      const name = u.name || [u.firstName, u.lastName].filter(Boolean).join(' ');
      const email = u.email || u.username || '';
      const phone = u.phone || u.contactNumber || '';
      return {
        value: id,
        label: (
          <div className="client-option">
            <div className="name">{name || 'Unnamed User'}</div>
            <div className="meta">{email}{phone ? ` • ${phone}` : ''}</div>
          </div>
        )
      };
    });
  }, [clientSuggestions]);

  return (
    <div className="client-details-form">
      <Divider orientation="left">Client Details</Divider>
      <div style={{ marginBottom: 16, padding: '8px 12px', background: '#f0f5ff', border: '1px solid #d6e4ff' }}>
        <small style={{ color: '#1890ff' }}>
          <InfoCircleOutlined style={{ marginRight: 6 }} />
          <strong>Note:</strong> Only client name is required. If email is provided, booking confirmation and review emails will be automatically sent to the client.
        </small>
      </div>
      
      <Row gutter={24}>
        <Col span={24} style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
          <Form.Item name="useExisting" valuePropName="checked" initialValue={false} style={{ marginBottom: 8 }}>
            <Checkbox
              onChange={e => {
                if (form && typeof form.setFieldsValue === 'function') {
                  if (!e.target.checked) {
                    // Clear all client fields when switching to manual entry
                    form.setFieldsValue({
                      clientUserId: undefined,
                      clientName: '',
                      clientEmail: '',
                      clientPhone: ''
                    });
                  } else {
                    // Also clear fields when switching to existing, except userId
                    form.setFieldsValue({
                      clientUserId: undefined,
                      clientName: '',
                      clientEmail: '',
                      clientPhone: ''
                    });
                  }
                }
              }}
            >
              Use existing client
            </Checkbox>
          </Form.Item>
        </Col>

        {/* Existing client selector */}
        <Col span={24}>
          <Form.Item noStyle shouldUpdate={(prev, curr) => prev.useExisting !== curr.useExisting}>
            {({ getFieldValue }) => (
              getFieldValue('useExisting') ? (
                <>
                  <Row gutter={24}>
                    <Col span={16}>
                      <Form.Item label="Find Client" required tooltip="Search by name or email">
                        <Select
                          showSearch
                          placeholder="Type name or email to search..."
                          filterOption={false}
                          onSearch={handleSearch}
                          onChange={handleSelectClient}
                          notFoundContent={loadingClients ? <Spin size="small" /> : 'No users'}
                          options={options}
                          allowClear
                        />
                      </Form.Item>
                    </Col>
                    <Form.Item name="clientUserId" hidden>
                      <Input />
                    </Form.Item>
                  </Row>
                </>
              ) : null
            )}
          </Form.Item>
        </Col>

        <Col span={8}>
          <Form.Item noStyle shouldUpdate={(prev, curr) => prev.useExisting !== curr.useExisting}>
            {({ getFieldValue }) => (
              <Form.Item
                name="clientName"
                label="Client Name"
                rules={[{ required: true, message: 'Please enter client name' }]}
              >
                <Input placeholder="Enter client name" disabled={getFieldValue('useExisting')} />
              </Form.Item>
            )}
          </Form.Item>
        </Col>

        <Col span={8}>
          <Form.Item noStyle shouldUpdate={(prev, curr) => prev.useExisting !== curr.useExisting}>
            {({ getFieldValue }) => (
              <Form.Item
                name="clientEmail"
                label="Client Email"
                rules={[
                  { type: 'email', message: 'Please enter valid email' }
                ]}
                tooltip="Email is optional. If provided, booking confirmation and review emails will be sent to client."
              >
                <Input placeholder="Enter client email" disabled={getFieldValue('useExisting')} />
              </Form.Item>
            )}
          </Form.Item>
        </Col>

        <Col span={8}>
          <Form.Item noStyle shouldUpdate={(prev, curr) => prev.useExisting !== curr.useExisting}>
            {({ getFieldValue }) => {
              const isExisting = !!getFieldValue('useExisting');
              return (
                <Form.Item
                  name="clientPhone"
                  label="Client Phone"
                  rules={isExisting ? [] : [{ validator: validatePhone }]}
                  validateTrigger="onBlur"
                  tooltip="Phone number is optional for record keeping."
                >
                  <Input placeholder="Enter client phone" disabled={isExisting} />
                </Form.Item>
              );
            }}
          </Form.Item>
        </Col>
      </Row>
    </div>
  );
};

export default ClientDetailsForm;
